<template>
  <div
    id="generic-modal"
    :style="
      getWindowWidthMobile ? 'width: 280px' : { width: `${this.width}px` }
    "
  >
    <div class="row no-gutters modal-header-wrapper">
      <div class="col">
        <slot
          name="modal-header"
          :hasSeparator="false"
          :hasExit="hasExit"
          :titleText="titleText"
          :titleStyle="titleStyle"
        ></slot>
      </div>
    </div>
    <div class="row no-gutters" :class="getWindowWidthMobile ? 'pl-4' : 'px-4'">
      <div class="col description-text">
        <p class="text" v-html="description" :lang="narratorLang" tabindex="-1" :aria-label="descriptionAriaLabel"/>
      </div>
    </div>
    <div v-if="time" class="row no-gutters px-4 mt-3">
      <div class="col time">
        <p class="text" :lang="narratorLang" tabindex="-1" :aria-label="`${ min }:${ sec }`">{{ min }}:{{ sec }}</p>
      </div>
    </div>
    <div
      v-if="descriptionEnd"
      class="row no-gutters mt-3"
      :class="getWindowWidthMobile ? 'pl-4' : 'px-4'"
    >
      <div class="col description-text">
        <p class="text" v-html="descriptionEnd" :lang="narratorLang" tabindex="-1" :aria-label="descriptionEndAriaLabel"/>
      </div>
    </div>
    <div
      class="row no-gutters text-center button-wrapper justify-content-end"
      ref="button-wrapper"
      :style="[
        okButtonMarginTop ? { 'margin-top': okButtonMarginTop } : '',
        okButtonMarginBottom ? { 'margin-bottom': okButtonMarginBottom } : ''
      ]"
    >
      <rounded-button
        v-if="!customButtons"
        :customStyle="{
          width: '85%',
          height: okButtonHeight ? okButtonHeight : '44px'
        }"
        :text="okText"
        :iconName="buttonIcon"
        :iconType="buttonIconType"
        :iconWidth="buttonIconWidht"
        :iconHeight="buttonIconHeight"
        :fontSize="okButtonFontSize"
        class="rounded-button"
        ref="rounded-button"
        @roundedButtonClicked="okAction"
      />
      <template v-if="customButtons">
        <div
          v-if="cancelAction"
          @click="cancelAction"
          class="col pl-4 custom-decline-button"
          tabindex="0"
          @keyup.enter="cancelAction"
          :lang="narratorLang"
          :aria-label="$t(cancelText)"
        >
          {{ $t(cancelText) }}
        </div>

        <div
          v-if="okAction"
          @click="okAction"
          class="col-auto custom-accept-button"
          ref="custom-accept-button"
          tabindex="0"
          @keyup.enter="okAction"
          :lang="narratorLang"
          :aria-label="$t(okText)"
        >
          {{ $t(okText) }}
        </div>
      </template>
    </div>
    <div
      v-if="cancelText && cancelAction && !customButtons"
      class="row no-gutters text-center button-wrapper justify-content-end"
    >
      <div class="col-12">
        <inline-link
          :text="cancelText"
          :isDisabled="false"
          @inlineLinkClicked="cancelAction"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RoundedButton from '@/components/shared/elements/buttons/RoundedButton';
import InlineLink from '@/components/shared/elements/buttons/InlineLink';
import ModalMixin from '@/components/shared/elements/mixins/ModalMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'GenericModal',
  components: {
    RoundedButton,
    InlineLink,
  },
  props: {
    titleText: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    okText: {
      type: String,
      required: true,
    },
    okAction: {
      type: Function,
    },
    cancelText: {
      type: String,
      required: false,
    },
    cancelAction: {
      type: Function,
      required: false,
    },
    escAction: {
      type: Function,
      required: false,
    },
    canBeClosedWithEsc: {
      type: Boolean,
      required: false,
      default: true,
    },
    width: {
      type: Number,
      default: 330,
    },
    hasExit: {
      type: Boolean,
      default: false,
    },
    customButtons: {
      type: Boolean,
      default: false,
      required: false,
    },
    cbMarginRight: {
      type: String,
      default: '24px',
      required: false,
    },
    cbMarginBottom: {
      type: String,
      default: '24px',
      required: false,
    },
    pressKeyToGo: {
      type: String,
      default: undefined,
      required: false,
    },
    time: {
      type: Number,
      default: undefined,
      required: false,
    },
    descriptionEnd: {
      type: String,
      default: undefined,
      required: false,
    },
    buttonIcon: {
      type: String,
      default: undefined,
      required: false,
    },
    buttonIconType: {
      type: String,
      default: undefined,
      required: false,
    },
    buttonIconWidht: {
      type: Number,
      default: undefined,
      required: false,
    },
    buttonIconHeight: {
      type: Number,
      default: undefined,
      required: false,
    },
    okButtonFontSize: {
      type: String,
      required: false,
    },
    okButtonMarginTop: {
      type: String,
      require: false,
    },
    okButtonMarginBottom: {
      type: String,
      require: false,
    },
    okButtonHeight: {
      type: String,
      require: false,
    },
    titleStyle: {
      type: [Object, Array],
      required: false,
    },
    needPreviousModal: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [ModalMixin],
  data: () => ({
    count: undefined,
    timerTimeOutInterval: undefined,
    lastUpdated: undefined,
    fullTimerInterval: undefined,
  }),
  created() {
    if (this.time) {
      this.count = this.time;
      this.lastUpdatedAt = this.$moment();
      this.visibilityChangeMethod();
      this.startFullTimer();
      document.addEventListener(
        'visibilitychange',
        this.visibilityChangeMethod,
      );
    }
  },
  mounted() {
    this.setFocusToOkButton();
    window.addEventListener('keyup', this.handleKeyUp);
    if (this.customButtons) {
      if (this.cbMarginRight) {
        this.$refs[
          'custom-accept-button'
        ].style.marginRight = this.cbMarginRight;
      }
      this.$refs['button-wrapper'].style.marginBottom = this.cbMarginBottom;
    }
  },
  beforeDestroy() {
    if (this.timerTimeOutInterval) {
      clearTimeout(this.timerTimeOutInterval);
      this.timerTimeOutInterval = null;
    }
    if (this.fullTimerInterval) {
      clearTimeout(this.fullTimerInterval);
      this.fullTimerInterval = null;
    }
    if (this.time) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeMethod,
      );
    }
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  methods: {
    setFocusToOkButton() {
      if (this.customButtons) {
        if (this.$refs['custom-accept-button']) { this.$refs['custom-accept-button'].focus(); }
      } else if (this.$refs['rounded-button']) { this.$refs['rounded-button'].focus(); }
    },
    startFullTimer() {
      this.fullTimerInterval = setTimeout(() => {
        if (!this.timerTimeOutInterval) {
          this.$emit('timedOut');
        }
      }, this.count);
    },
    visibilityChangeMethod() {
      if (document.visibilityState === 'visible') {
        this.appIsVisible();
      } else {
        this.appIsHided();
      }
    },
    appIsVisible() {
      if (this.lastUpdatedAt) {
        const now = this.$moment();
        const diffInMiliSeconds = now.diff(this.lastUpdatedAt);
        const roundedDiff = Math.round(diffInMiliSeconds / 1000) * 1000;
        if (roundedDiff >= this.count) {
          this.$emit('timedOut');
        } else {
          this.count -= Math.round(roundedDiff);
          this.countTimer();
        }
      }
    },
    appIsHided() {
      if (this.timerTimeOutInterval) {
        clearTimeout(this.timerTimeOutInterval);
        this.timerTimeOutInterval = null;
      }
    },
    handleKeyUp(event) {
      if (this.pressKeyToGo && event.key === this.pressKeyToGo) {
        this.okAction();
      }
      if (this.escAction && event.key === 'Escape') {
        this.escAction();
        return;
      }

      if (event.key === 'Escape') {
        if (this.canBeClosedWithEsc === false) return;

        setTimeout(() => {
          this.$eventBus.closeModal();
        }, 200);
      }
    },
    countTimer() {
      if (this.count > 0) {
        this.timerTimeOutInterval = setTimeout(() => {
          this.count -= 1000;
          this.lastUpdatedAt = this.$moment();
          this.countTimer();
        }, 1000);
      }
      if (this.count <= 0) {
        if (this.fullTimerInterval) {
          clearTimeout(this.fullTimerInterval);
          this.fullTimerInterval = null;
        }
        this.$emit('timedOut');
      }
    },
  },
  computed: {
    ...mapGetters({
      getWindowWidthMobile: 'settings/getWindowWidthMobile',
      narratorLang: 'settings/getNarratorLanguage',
    }),
    descriptionAriaLabel() {
      const parser = new DOMParser();
      const parsedText = parser.parseFromString(this.description, 'text/html');
      return parsedText.documentElement.textContent;
    },
    descriptionEndAriaLabel() {
      const parser = new DOMParser();
      const parsedText = parser.parseFromString(this.descriptionEnd, 'text/html');
      return parsedText.documentElement.textContent;
    },
    min() {
      const m = Math.trunc(this.count / 1000 / 60) % 60;
      return m > 9 ? m : '0'.concat(m);
    },
    sec() {
      const s = Math.trunc(this.count / 1000) % 60;
      return s > 9 ? s : '0'.concat(s);
    },
  },
};
</script>

<style lang="scss" scoped>
#generic-modal {
  .modal-header-wrapper {
    margin-top: 24px;
    margin-bottom: 18px;
  }
  .description-text {
    font-family: Roboto;
    font-size: 16px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.38;
    letter-spacing: normal;
    text-align: left;
    color: var(--terraGrey);
    margin-bottom: 10px;
    word-break: break-word;
    .text {
      word-break: break-word;
    }
  }
  .time {
    font-family: Roboto;
    font-size: 16px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.38;
    letter-spacing: normal;
    text-align: left;
    color: var(--terraGrey);
    margin-bottom: 10px;
    word-break: break-word;
    font-weight: bold;
  }
  .custom-margin {
    margin-right: 1.5rem;
  }
  .cl-button {
    font-family: Roboto;
    font-size: 16px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.21;
    letter-spacing: normal;
    text-align: center;
    color: var(--primaryBlue);
    cursor: pointer;
  }
  .dark-red {
    color: color("darkRed");
  }
  .rounded-button {
    /deep/ .button-wrapper {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }
    /deep/ .btn {
      border-radius: 26px;
      width: 240px;
      margin: 0 auto;
      .btn-text {
        font-family: Roboto;
        font-size: 16px;
        color: var(--white);
        padding-top: 10px;
        padding-bottom: 10px;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        display: block !important;
        padding: 0 !important;
      }
    }
  }
  .button-wrapper {
    margin-top: 16px;
    margin-bottom: 18px;
  }
}
.custom-decline-button {
  font-size: 18px;
  font-weight: bold;
  line-height: 1.22;
  color: #ff4444;
  text-align: right;
  margin-left: auto;
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: center;
}
.custom-accept-button {
  // margin-right: 36px;
  margin-left: 48px;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.22;
  color: var(--primaryBlue);
  text-align: right;
  cursor: pointer;
}

@media (max-width: $medium-screen) {
  #generic-modal {
    max-height: 500px;
    .description-text {
      width: 100%;
      max-height: 300px;
      overflow-y: scroll;
      padding-right: 1.5rem;
    }
  }
}
</style>
